/*
 * @Copyright 2020. Institute for Future Intelligence, Inc.
 * A digital twin of the Rainbow HAT for Raspberry Pi
 * @author Charles Xie
 */

// @ts-ignore
import rainbowHatImage from "../img/rainbow-hat.png";
import {database, flowchart} from "../Main";
import {Port} from "./Port";
import {BoardBlock} from "./BoardBlock";
import {Block} from "./Block";
import {Util} from "../Util";
import {LedLight} from "./electronics/LedLight";
import {Button} from "./electronics/Button";
import {Buzzer} from "./electronics/Buzzer";
import {Sensor} from "./electronics/Sensor";
import {LedDisplay} from "./electronics/LedDisplay";
import {CheckBox} from "./electronics/CheckBox";

export class RainbowHatBlock extends BoardBlock {

  private static boardImageElement: HTMLImageElement;

  syncToggleSwitch: CheckBox;
  redLedLight: LedLight;
  greenLedLight: LedLight;
  blueLedLight: LedLight;
  buttonA: Button;
  buttonB: Button;
  buttonC: Button;
  buzzer: Buzzer;
  temperatureSensor: Sensor;
  barometricPressureSensor: Sensor;
  rgbLedLights: LedLight[] = [];
  alphanumericDisplays: LedDisplay[] = [];
  decimalPointDisplays: LedDisplay[] = [];

  private readonly portPiezoBuzzer: Port;
  private readonly portButtonA: Port;
  private readonly portButtonB: Port;
  private readonly portButtonC: Port;
  private readonly portRedLed: Port;
  private readonly portGreenLed: Port;
  private readonly portBlueLed: Port;
  private readonly portTemperatureSensor: Port;
  private readonly portPressureSensor: Port;
  private readonly portRgbLeds: Port[];
  private readonly portDisplay: Port;
  private readonly portClock: Port;

  private mouseOverObject: any;
  private boardImageX: number = 0;
  private boardImageY: number = 0;
  private clockTicking: boolean = false;
  private clockValue: number = -1;
  private displayType: string = "Any";
  private dontUpdateRemote: boolean = false;

  static State = class {
    readonly uid: string;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly remoteKey: string;
    readonly displayType: string;

    constructor(block: RainbowHatBlock) {
      this.uid = block.uid;
      this.x = block.x;
      this.y = block.y;
      this.width = block.width;
      this.height = block.height;
      this.remoteKey = block.remoteKey;
      this.displayType = block.displayType;
    }
  };

  constructor(uid: string, x: number, y: number, width: number, height: number) {
    super(uid, x, y, width, height);
    this.name = "Rainbow HAT";
    this.color = "#A9A9A9";
    this.marginX = 30;
    this.remoteKey = "rainbow-hat-default";

    if (RainbowHatBlock.boardImageElement === undefined) {
      RainbowHatBlock.boardImageElement = new Image();
      RainbowHatBlock.boardImageElement.src = rainbowHatImage;
      RainbowHatBlock.boardImageElement.onload = () => flowchart.blockView.requestDraw();
    }

    let dy = this.height / 14;
    this.portRedLed = new Port(this, true, "RL", 0, dy, false); // coordinates relative to this block
    this.portGreenLed = new Port(this, true, "GL", 0, 2 * dy, false);
    this.portBlueLed = new Port(this, true, "BL", 0, 3 * dy, false);
    this.portPiezoBuzzer = new Port(this, true, "PB", 0, 4 * dy, false);
    this.portRgbLeds = [];
    for (let i = 0; i < 7; i++) {
      let p = new Port(this, true, "L" + (i + 1), 0, (i + 5) * dy, false);
      this.portRgbLeds.push(p);
      this.ports.push(p);
    }
    this.portDisplay = new Port(this, true, "DP", 0, 12 * dy, false);
    this.portClock = new Port(this, true, "CK", 0, 13 * dy, false);
    this.ports.push(this.portRedLed);
    this.ports.push(this.portGreenLed);
    this.ports.push(this.portBlueLed);
    this.ports.push(this.portPiezoBuzzer);
    this.ports.push(this.portDisplay);
    this.ports.push(this.portClock);

    dy = this.height / 6;
    this.portTemperatureSensor = new Port(this, false, "TS", this.width, dy, true);
    this.portPressureSensor = new Port(this, false, "PS", this.width, 2 * dy, true);
    this.portButtonA = new Port(this, false, "BA", this.width, 3 * dy, true);
    this.portButtonB = new Port(this, false, "BB", this.width, 4 * dy, true);
    this.portButtonC = new Port(this, false, "BC", this.width, 5 * dy, true);

    this.ports.push(this.portTemperatureSensor);
    this.ports.push(this.portPressureSensor);
    this.ports.push(this.portButtonA);
    this.ports.push(this.portButtonB);
    this.ports.push(this.portButtonC);

    this.initComponents();
    this.linkRemote();
  }

  private initComponents(): void {
    this.syncToggleSwitch = new CheckBox("SY", 230, 12.5, 48, 16);
    this.redLedLight = new LedLight("RL", "red", 4, 16, 25, 65, 233, 18, 8);
    this.greenLedLight = new LedLight("GL", "green", 4, 16, 25, 147, 233, 18, 8);
    this.blueLedLight = new LedLight("BL", "blue", 4, 16, 25, 230, 233, 18, 8);
    this.buttonA = new Button("BA", 38, 245, 72, 24);
    this.buttonB = new Button("BB", 120, 245, 72, 24);
    this.buttonC = new Button("BC", 203, 245, 72, 24);
    this.buzzer = new Buzzer("Piezo Buzzer", 35, 170, 20, 20);
    this.temperatureSensor = new Sensor("TS", "°C", 152, 108, 12, 12);
    this.barometricPressureSensor = new Sensor("PS", "hPa", 187, 115, 20, 10);
    this.rgbLedLights.push(new LedLight("L1", "black", 16, 12, 2, 251, 78, 20, 20));
    this.rgbLedLights.push(new LedLight("L2", "black", 16, 12, 2, 218, 62, 20, 20));
    this.rgbLedLights.push(new LedLight("L3", "black", 16, 12, 2, 183, 53, 20, 20));
    this.rgbLedLights.push(new LedLight("L4", "black", 16, 12, 2, 147, 50, 20, 20));
    this.rgbLedLights.push(new LedLight("L5", "black", 16, 12, 2, 111, 53, 20, 20));
    this.rgbLedLights.push(new LedLight("L6", "black", 16, 12, 2, 76, 62, 20, 20));
    this.rgbLedLights.push(new LedLight("L7", "black", 16, 12, 2, 44, 78, 20, 20));
    this.alphanumericDisplays.push(new LedDisplay("D1", 34, 214, 33, 65));
    this.alphanumericDisplays.push(new LedDisplay("D2", 95, 214, 33, 65));
    this.alphanumericDisplays.push(new LedDisplay("D3", 156, 214, 33, 65));
    this.alphanumericDisplays.push(new LedDisplay("D4", 218, 214, 33, 65));
    this.decimalPointDisplays.push(new LedDisplay("D5", 78, 214, 33, 65));
    this.decimalPointDisplays.push(new LedDisplay("D6", 139, 214, 33, 65));
    this.decimalPointDisplays.push(new LedDisplay("D7", 200, 214, 33, 65));
    this.decimalPointDisplays.push(new LedDisplay("D8", 261, 214, 33, 65));
    for (let display of this.decimalPointDisplays) {
      display.fontSize = "40px";
    }
  }

  getCopy(): Block {
    let block = new RainbowHatBlock(name + " #" + Date.now().toString(16), this.x, this.y, this.width, this.height);
    block.width = this.width;
    block.height = this.height;
    block.remoteKey = this.remoteKey;
    block.displayType = this.displayType;
    return block;
  }

  setDisplayType(displayType: string): void {
    this.displayType = displayType;
  }

  getDisplayType(): string {
    return this.displayType;
  }

  reset(): void {
    this.clearSensorData();
    this.clockValue = -1;
  }

  destroy(): void {
    this.synched = false;
    if (this.remoteKey) database.reference.ref(this.remoteKey).off();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    super.draw(ctx);
    if (this.iconic) return;
    ctx.save();
    this.boardImageX = this.x + this.marginX + 5;
    this.boardImageY = this.y + this.marginY + 5;
    ctx.translate(this.boardImageX, this.boardImageY);
    ctx.drawImage(RainbowHatBlock.boardImageElement, 0, 0);
    this.syncToggleSwitch.draw(ctx);
    this.redLedLight.draw(ctx);
    this.greenLedLight.draw(ctx);
    this.blueLedLight.draw(ctx);
    this.buttonA.draw(ctx);
    this.buttonB.draw(ctx);
    this.buttonC.draw(ctx);
    this.buzzer.draw(ctx);
    this.temperatureSensor.draw(ctx);
    this.barometricPressureSensor.draw(ctx);
    this.drawToolTips(ctx);
    for (let light of this.rgbLedLights) {
      light.draw(ctx);
    }
    for (let display of this.alphanumericDisplays) {
      display.draw(ctx);
    }
    for (let display of this.decimalPointDisplays) {
      display.draw(ctx);
    }
    ctx.restore();
  }

  private drawToolTips(ctx: CanvasRenderingContext2D): void {
    let x = 0;
    let y = -25;
    if (this.mouseOverObject == this.redLedLight) {
      x += this.redLedLight.x + this.redLedLight.width / 2;
      y += this.redLedLight.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Red LED light', true);
    } else if (this.mouseOverObject == this.greenLedLight) {
      x += this.greenLedLight.x + this.greenLedLight.width / 2;
      y += this.greenLedLight.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Green LED light', true);
    } else if (this.mouseOverObject == this.blueLedLight) {
      x += this.blueLedLight.x + this.blueLedLight.width / 2;
      y += this.blueLedLight.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Blue LED light', true);
    } else if (this.mouseOverObject == this.buttonA) {
      x += this.buttonA.x + this.buttonA.width / 2;
      y += this.buttonA.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Button A', true);
    } else if (this.mouseOverObject == this.buttonB) {
      x += this.buttonB.x + this.buttonB.width / 2;
      y += this.buttonB.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Button B', true);
    } else if (this.mouseOverObject == this.buttonC) {
      x += this.buttonC.x + this.buttonC.width / 2;
      y += this.buttonC.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Button C', true);
    } else if (this.mouseOverObject == this.temperatureSensor) {
      x += this.temperatureSensor.x + this.temperatureSensor.width / 2;
      y += this.temperatureSensor.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Temperature sensor', true);
    } else if (this.mouseOverObject == this.barometricPressureSensor) {
      x += this.barometricPressureSensor.x + this.barometricPressureSensor.width / 2;
      y += this.barometricPressureSensor.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Barometric pressure sensor', true);
    } else if (this.mouseOverObject == this.syncToggleSwitch) {
      x += this.syncToggleSwitch.x + this.syncToggleSwitch.width / 2;
      y += this.syncToggleSwitch.y;
      ctx.drawTooltip(x, y, 20, 8, 10, 'Sync Toggle Switch (' + (this.synched ? 'On' : 'Off') + ')', true);
    }
  }

  clearSensorData(): void {
    this.temperatureSensor.clear();
    this.barometricPressureSensor.clear();
  }

  turnoff(): void {
    this.redLedLight.on = false;
    this.greenLedLight.on = false;
    this.blueLedLight.on = false;
    for (let light of this.rgbLedLights) {
      light.on = false;
    }
    for (let i = 0; i < 4; i++) {
      this.alphanumericDisplays[i].setCharacter(null);
      this.decimalPointDisplays[i].setCharacter(null);
    }
    flowchart.blockView.requestDraw();
  }

  refreshView(): void {
    super.refreshView();
    let dy = this.height / 14;
    this.portRedLed.setY(dy); // coordinates relative to this block
    this.portGreenLed.setY(2 * dy);
    this.portBlueLed.setY(3 * dy);
    this.portPiezoBuzzer.setY(4 * dy);
    for (let i = 0; i < 7; i++) {
      this.portRgbLeds[i].setY((i + 5) * dy);
    }
    this.portDisplay.setY(12 * dy);
    this.portClock.setY(13 * dy);
    dy = this.height / 6;
    this.portTemperatureSensor.setX(this.width);
    this.portTemperatureSensor.setY(dy);
    this.portPressureSensor.setX(this.width);
    this.portPressureSensor.setY(2 * dy);
    this.portButtonA.setX(this.width);
    this.portButtonA.setY(3 * dy);
    this.portButtonB.setX(this.width);
    this.portButtonB.setY(4 * dy);
    this.portButtonC.setX(this.width);
    this.portButtonC.setY(5 * dy);
  }

  mouseDown(e: MouseEvent): boolean {
    let rect = flowchart.blockView.canvas.getBoundingClientRect();
    let dx = e.clientX - rect.x - this.boardImageX;
    let dy = e.clientY - rect.y - this.boardImageY;
    if (this.buttonA.contains(dx, dy)) {
      this.buttonA.on = true;
      flowchart.traverse(this);
      //this.buzzer.beepButton("A");
      this.redLedLight.on = true;
      this.updateRemote({redLed: true, buttonA: true, cyber: true});
      return true;
    }
    if (this.buttonB.contains(dx, dy)) {
      this.buttonB.on = true;
      flowchart.traverse(this);
      //this.buzzer.beepButton("B");
      this.greenLedLight.on = true;
      this.updateRemote({greenLed: true, buttonB: true, cyber: true});
      return true;
    }
    if (this.buttonC.contains(dx, dy)) {
      this.buttonC.on = true;
      flowchart.traverse(this);
      //this.buzzer.beepButton("C");
      this.blueLedLight.on = true;
      this.updateRemote({blueLed: true, buttonC: true, cyber: true});
      return true;
    }
    return false;
  }

  mouseUp(e: MouseEvent): void {
    let rect = flowchart.blockView.canvas.getBoundingClientRect();
    let dx = e.clientX - rect.x - this.boardImageX;
    let dy = e.clientY - rect.y - this.boardImageY;
    if (this.buttonA.contains(dx, dy)) {
      this.buttonA.on = false;
      flowchart.traverse(this);
      this.redLedLight.on = false;
      this.updateRemote({redLed: false, buttonA: false, cyber: true});
      return;
    }
    if (this.buttonB.contains(dx, dy)) {
      this.buttonB.on = false;
      flowchart.traverse(this);
      this.greenLedLight.on = false;
      this.updateRemote({greenLed: false, buttonB: false, cyber: true});
      return;
    }
    if (this.buttonC.contains(dx, dy)) {
      this.buttonC.on = false;
      flowchart.traverse(this);
      this.blueLedLight.on = false;
      this.updateRemote({blueLed: false, buttonC: false, cyber: true});
      return;
    }
    if (this.syncToggleSwitch.contains(dx, dy)) {
      this.syncToggleSwitch.on = !this.syncToggleSwitch.on;
      this.synched = !this.synched;
      flowchart.traverse(this);
      return;
    }
  }

  mouseMove(e: MouseEvent): void {
    let canvas = flowchart.blockView.canvas;
    let rect = canvas.getBoundingClientRect();
    let dx = e.clientX - rect.x - this.boardImageX;
    let dy = e.clientY - rect.y - this.boardImageY;
    if (this.redLedLight.contains(dx, dy)) {
      this.mouseOverObject = this.redLedLight;
      canvas.style.cursor = "pointer";
    } else if (this.greenLedLight.contains(dx, dy)) {
      this.mouseOverObject = this.greenLedLight;
      canvas.style.cursor = "pointer";
    } else if (this.blueLedLight.contains(dx, dy)) {
      this.mouseOverObject = this.blueLedLight;
      canvas.style.cursor = "pointer";
    } else if (this.buttonA.contains(dx, dy)) {
      this.mouseOverObject = this.buttonA;
      canvas.style.cursor = "pointer";
    } else if (this.buttonB.contains(dx, dy)) {
      this.mouseOverObject = this.buttonB;
      canvas.style.cursor = "pointer";
    } else if (this.buttonC.contains(dx, dy)) {
      this.mouseOverObject = this.buttonC;
      canvas.style.cursor = "pointer";
    } else if (this.temperatureSensor.contains(dx, dy)) {
      this.mouseOverObject = this.temperatureSensor;
      canvas.style.cursor = "pointer";
    } else if (this.barometricPressureSensor.contains(dx, dy)) {
      this.mouseOverObject = this.barometricPressureSensor;
      canvas.style.cursor = "pointer";
    } else if (this.syncToggleSwitch.contains(dx, dy)) {
      this.mouseOverObject = this.syncToggleSwitch;
      canvas.style.cursor = "pointer";
    } else {
      let onRgbLedLight = false;
      for (let light of this.rgbLedLights) {
        if (light.contains(dx, dy)) {
          this.mouseOverObject = light;
          canvas.style.cursor = "pointer";
          onRgbLedLight = true;
          break;
        }
      }
      if (!onRgbLedLight) {
        this.mouseOverObject = null;
        canvas.style.cursor = "default";
      }
    }
    flowchart.blockView.requestDraw();
  }

  mouseLeave(e: MouseEvent): void {
  }

  updateModel(): void {

    let clock = this.portClock.getValue();
    this.clockTicking = clock && clock > this.clockValue;
    if (this.clockTicking) {
      this.clockValue = clock;
    }
    let displayType = this.portDisplay.getValue();
    if (displayType !== undefined) {
      this.displayType = displayType;
      if (!this.clockTicking) this.updateDisplay();
    }

    // sensors
    if (this.clockTicking) {
      this.portTemperatureSensor.setValue(this.temperatureSensor.getData());
      this.portPressureSensor.setValue(this.barometricPressureSensor.getData());
    }

    // buttons
    this.portButtonA.setValue(this.buttonA.isSelected());
    this.portButtonB.setValue(this.buttonB.isSelected());
    this.portButtonC.setValue(this.buttonC.isSelected());

    // rgb LED lights
    let rgbaLeds = [];
    for (let i = 0; i < 7; i++) {
      let v = this.portRgbLeds[i].getValue();
      rgbaLeds.push(v);
      this.rgbLedLights[i].on = v !== undefined ? v[0] > 0 || v[1] > 0 || v[2] > 0 : false;
      this.rgbLedLights[i].color = v !== undefined ? Util.rgbaToHex(v[0], v[1], v[2], v[3]) : "#000000";
    }

    this.updateConnectors();

    // update the cyber twin
    let inputRedLed = this.portRedLed.getValue();
    let inputGreenLed = this.portGreenLed.getValue();
    let inputBlueLed = this.portBlueLed.getValue();
    this.redLedLight.on = inputRedLed;
    this.greenLedLight.on = inputGreenLed;
    this.blueLedLight.on = inputBlueLed;

    // update the physical twin
    let rgbArray = []; // TODO: alpha not supported yet, so we can't use rgbaLeds directly
    for (let rgba of rgbaLeds) {
      if (rgba === undefined) {
        rgbArray.push(Util.rgbToHex(0, 0, 0));
      } else {
        rgbArray.push(Util.rgbToHex(rgba[0], rgba[1], rgba[2]));
      }
    }

    if (!this.dontUpdateRemote) {
      this.updateRemote({
        rainbowRgb: rgbArray,
        redLed: inputRedLed ? inputRedLed : false,
        greenLed: inputGreenLed ? inputGreenLed : false,
        blueLed: inputBlueLed ? inputBlueLed : false
      });
    }
    flowchart.blockView.requestDraw();
  }

  updateRemote(value): void {
    if (this.synched) {
      if (this.remoteKey) {
        database.reference.ref(this.remoteKey).update(value);
      }
    } else {
      if (this.clockTicking) { // use simulated temperature and pressure data
        this.temperatureSensor.addData(20 + Math.random() * 2);
        this.barometricPressureSensor.addData(900 + Math.random() * 10);
        this.updateDisplay();
      }
    }
  }

  linkRemote(): void {
    if (this.remoteKey) {
      database.reference.ref(this.remoteKey).on("value", snapshot => {
        if (this.synched) {
          let data = snapshot.val();
          if (!data.cyber) {
            this.redLedLight.on = data.redLed;
            this.greenLedLight.on = data.greenLed;
            this.blueLedLight.on = data.blueLed;
            this.buttonA.on = data.buttonA;
            this.buttonB.on = data.buttonB;
            this.buttonC.on = data.buttonC;
            if (data.rainbowRgb) {
              for (let i = 0; i < this.rgbLedLights.length; i++) {
                this.rgbLedLights[i].on = data.rainbowRgb[i] !== "#000000";
                this.rgbLedLights[i].color = data.rainbowRgb[i];
              }
            }
            this.dontUpdateRemote = true;
            flowchart.traverse(this);
            flowchart.blockView.requestDraw();
            this.dontUpdateRemote = false;
          }
          if (data.allowTemperatureTransmission) {
            this.temperatureSensor.collectionInterval = data.sensorDataCollectionInterval ? data.sensorDataCollectionInterval * 0.001 : 1;
            if (data.temperature !== this.temperatureSensor.getLatestData()) {
              let temp: number = <number>data.temperature;
              this.temperatureSensor.addData(temp);
              this.setAlphaNumericDisplays(temp);
            }
          }
          if (data.allowBarometricPressureTransmission) {
            this.barometricPressureSensor.collectionInterval = data.sensorDataCollectionInterval ? data.sensorDataCollectionInterval * 0.001 : 1;
            if (data.barometricPressure !== this.barometricPressureSensor.getLatestData()) {
              let pres: number = <number>data.barometricPressure;
              this.barometricPressureSensor.addData(pres);
              this.setAlphaNumericDisplays(pres);
            }
          }
        }
      });
    }
  }

  private updateDisplay(): void {
    switch (this.displayType) {
      case "Temperature":
        this.setAlphaNumericDisplays(this.temperatureSensor.getLatestData());
        break;
      case "Pressure":
        this.setAlphaNumericDisplays(this.barometricPressureSensor.getLatestData());
        break;
    }
  }

  private setAlphaNumericDisplays(value: number): void {
    let i: number;
    for (i = 0; i < 4; i++) {
      this.alphanumericDisplays[i].setCharacter(null);
      this.decimalPointDisplays[i].setCharacter(null);
    }
    if (value === undefined) return;
    let s: string = value.toString();
    i = s.indexOf(".");
    if (i > 0 && i <= 4) {
      this.decimalPointDisplays[i - 1].setCharacter(".");
    }
    let integerPart: string = s.substring(0, i);
    let decimalPart: string = s.substring(i + 1);
    s = (integerPart + decimalPart).substr(0, 4);
    for (i = 0; i < 4; i++) {
      this.alphanumericDisplays[i].setCharacter(s[i]);
    }
  }

}
